import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { GAME_MODE_CATALOG, type GameMode } from '@interactive-room';
import { ACTIVITY_PRESETS, type ActivityPreset } from '@interactive-room/catalog/activityPresets';
import { listActivityDrafts, deleteActivityDraft, type ActivityDraft } from '../../studio/activityStorage';
import { studioHubUrl, studioModeEditorUrl } from '../../studio/consoleStudioLinks';
import { MODE_VISUALS } from '../../theme/designSystem';

interface ActivityModeCatalogProps {
  mode: GameMode;
  stagedId?: string | null;
  subjectId?: string;
  onBack: () => void;
  /** Edición rápida de emergencia — solo borradores existentes. */
  onEditDraft: (draft: ActivityDraft) => void;
  onStageDraft: (draft: ActivityDraft) => void;
  onStagePreset: (preset: ActivityPreset) => void;
}

export function ActivityModeCatalog({
  mode,
  stagedId,
  subjectId,
  onBack,
  onEditDraft,
  onStageDraft,
  onStagePreset,
}: ActivityModeCatalogProps) {
  const [refresh, setRefresh] = useState(0);
  void refresh;

  const meta = GAME_MODE_CATALOG[mode];
  const visual = MODE_VISUALS[mode];
  const drafts = useMemo(() => listActivityDrafts(mode), [mode, refresh]);
  const presets = useMemo(() => ACTIVITY_PRESETS.filter((p) => p.mode === mode), [mode]);
  const studioHref = studioHubUrl(subjectId);
  const studioModeHref = studioModeEditorUrl(mode, subjectId);

  const handleDelete = (id: string) => {
    deleteActivityDraft(id);
    setRefresh((n) => n + 1);
  };

  return (
    <div className="sala-catalog flex min-h-0 flex-1 flex-col">
      <button type="button" className="sala-catalog__back" onClick={onBack}>
        ❮ Modalidades
      </button>

      <div className="sala-catalog__head">
        <span className="text-lg">{meta.icon}</span>
        <div>
          <p className="font-display text-sm font-bold text-[var(--sala-ink)]">{meta.displayName} guardados</p>
          <p className="text-[10px] text-[var(--sala-ink-dim)]">{meta.tagline}</p>
        </div>
      </div>

      <div className="sala-catalog__actions mb-3 flex flex-col gap-2">
        <Link to={studioHref} className="sala-btn-hero w-full text-center text-xs">
          Crear nueva actividad en Studio
        </Link>
        <Link to={studioModeHref} className="sala-btn-outline w-full text-center text-xs">
          Abrir taller de {meta.displayName} en Studio
        </Link>
      </div>

      <div className="sala-catalog__scroll min-h-0 flex-1 overflow-y-auto">
        {drafts.length === 0 && presets.length === 0 ? (
          <div className="sala-empty-state">
            <p className="sala-empty-state__title">Sin actividades de {meta.displayName}</p>
            <p className="sala-empty-state__hint">
              Prepara borradores en Studio. Aquí solo eliges y transmites lo ya guardado.
            </p>
            <Link to={studioModeHref} className="sala-btn-outline mt-3 inline-flex text-xs">
              Crear en Studio
            </Link>
          </div>
        ) : (
          <>
            {drafts.length > 0 && (
              <section className="sala-catalog__section">
                <p className="sala-label mb-2">Mis borradores</p>
                <div className="space-y-1">
                  {drafts.map((draft) => (
                    <div
                      key={draft.id}
                      className={`sala-catalog__item flex items-center gap-1${stagedId === draft.id ? ' sala-catalog__item--staged' : ''}`}
                      style={{ background: visual.tint }}
                    >
                      <button
                        type="button"
                        className="flex min-w-0 flex-1 items-center gap-2 px-2 py-2 text-left"
                        onClick={() => onStageDraft(draft)}
                      >
                        <span className="text-base">📄</span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-semibold text-[var(--sala-ink)]">
                            {draft.title}
                          </span>
                          <span className="block truncate text-[10px] text-[var(--sala-ink-dim)]">
                            {draft.subjectTag}
                          </span>
                        </span>
                        {stagedId === draft.id && (
                          <span className="sala-catalog__staged-pill">Seleccionada</span>
                        )}
                      </button>
                      <button
                        type="button"
                        className="sala-btn-outline shrink-0 px-2 py-1 text-[9px]"
                        onClick={() => onEditDraft(draft)}
                        title="Edición rápida de emergencia"
                      >
                        ✏️
                      </button>
                      <button
                        type="button"
                        className="sala-catalog__delete mr-1"
                        onClick={() => handleDelete(draft.id)}
                        aria-label="Eliminar"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {presets.length > 0 && (
              <section className="sala-catalog__section">
                <p className="sala-label mb-2">Plantillas</p>
                <div className="space-y-1">
                  {presets.map((preset) => (
                    <button
                      key={preset.id}
                      type="button"
                      className="sala-catalog__item"
                      style={{ background: visual.tint }}
                      onClick={() => onStagePreset(preset)}
                    >
                      <span className="text-base">{preset.icon}</span>
                      <span className="min-w-0 flex-1 text-left">
                        <span className="block truncate text-sm font-semibold text-[var(--sala-ink)]">
                          {preset.title}
                        </span>
                        <span className="line-clamp-2 text-[10px] leading-snug text-[var(--sala-ink-dim)]">
                          {preset.description}
                        </span>
                      </span>
                    </button>
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </div>
    </div>
  );
}
